import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { FileText, LogOut, Globe, Menu, X, PlaneTakeoff } from 'lucide-react';

const Sidebar = ({ extraNav }) => {
  const { user, logout } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const content = (
    <div className="flex flex-col h-full p-6">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <img src="https://aercaribe.com/wp-content/uploads/2024/07/Logo-Aer-Cargo-blanco.webp" alt="AerCaribe Cargo" className="h-10 object-contain" />
        </div>
        <button
          className="lg:hidden text-white/80 hover:text-white"
          onClick={() => setOpen(false)}
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <nav className="flex-1 space-y-2">
        <button
          onClick={() => { navigate('/dashboard'); setOpen(false); }}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white transition-colors ${location.pathname === '/dashboard' ? 'bg-white/10' : 'hover:bg-white/10'}`}
        >
          <FileText className="h-5 w-5" />
          <span className="font-medium text-sm uppercase tracking-wide">{t('newsManagement')}</span>
        </button>
        <button
          onClick={() => { navigate('/routes'); setOpen(false); }}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white transition-colors ${location.pathname === '/routes' ? 'bg-white/10' : 'hover:bg-white/10'}`}
        >
          <PlaneTakeoff className="h-5 w-5" />
          <span className="font-medium text-sm uppercase tracking-wide">{language === 'es' ? 'Rutas' : 'Routes'}</span>
        </button>
        {extraNav}
      </nav>

      <div className="space-y-4 pt-4 border-t border-white/20">
        <div className="px-4 py-2">
          <p className="text-xs text-white/60 uppercase tracking-wider">{language === 'es' ? 'Usuario' : 'User'}</p>
          <p className="text-sm font-medium truncate text-white">{user?.name || user?.userId}</p>
        </div>
        <Button
          variant="ghost"
          onClick={toggleLanguage}
          className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10 gap-2"
          data-testid="sidebar-language-toggle"
        >
          <Globe className="h-4 w-4" />
          {language === 'es' ? 'English' : 'Español'}
        </Button>
        <Button
          variant="ghost"
          onClick={handleLogout}
          className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10 gap-2"
          data-testid="logout-button"
        >
          <LogOut className="h-4 w-4" />
          {t('logout')}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile topbar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-[#1E3A5F] flex items-center px-4 z-50">
        <button
          className="text-white mr-4"
          onClick={() => setOpen(true)}
          data-testid="hamburger-menu"
        >
          <Menu className="h-6 w-6" />
        </button>
        <img src="https://aercaribe.com/wp-content/uploads/2024/07/Logo-Aer-Cargo-blanco.webp" alt="AerCaribe Cargo" className="h-8 object-contain" />
      </div>

      {/* Mobile overlay */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <aside className={`
        lg:hidden fixed top-0 left-0 h-full w-64 bg-[#1E3A5F] z-50
        transform transition-transform duration-200
        ${open ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {content}
      </aside>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 h-full w-64 bg-[#1E3A5F] flex-col z-50">
        {content}
      </aside>
    </>
  );
};

export default Sidebar;
